import apiService from "services/api";
import { CustomModal, CustomTable } from "components";
import {
  studentLessonRecordTableActions,
  myLessonRecordsTableColumn,
  parseDbDataToFormInput,
} from "./constants";
import {
  addStudentRecordModalReducer,
  addStudentRecordReducerActions,
  initialAddStudentRecordState,
  initialStudentRecordTableModalState,
  studentRecordTableModalReducer,
  studentRecordTableModalReducerActions,
} from "./reducers";
import { tabs } from "./LessonRecordTabs";
import { TabContent, Nav, NavItem, NavLink, Button } from "reactstrap";
import { useReducer, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNotificationContext } from "contexts";
import { AddStudentReportPage } from "pages/Schedule/AddStudentReportPage";
import { FilterableTable } from "components/FilterableTable";
import {
  UploadStudentRecordModal,
  uploadStudentRecordModalActions,
  useUploadStudentRecordModalReducer,
} from "./UploadStudentRecordAssetModal";

const MyLessonRecords = () => {
  const { createNotification } = useNotificationContext();

  const formMethods = useForm({ reValidateMode: "onChange" });

  const [refetchCheck, setRefetchCheck] = useState(false);
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
  } = formMethods;

  const [studentRecordTableModalState, studentRecordTableModalDispatch] =
    useReducer(
      studentRecordTableModalReducer,
      initialStudentRecordTableModalState,
    );

  const [addStudentRecordModalState, addStudentRecordModalDispatch] =
    useReducer(addStudentRecordModalReducer, initialAddStudentRecordState);

  const [uploadStudentRecordModalState, uploadStudentRecordModalDispatch] =
    useUploadStudentRecordModalReducer();

  const { studentRecord } = studentRecordTableModalState;

  const initialTab = tabs[0].index;
  const [tabToShow, setTabToShow] = useState(initialTab);
  const switchTab = (tabNumber) => () =>
    tabNumber !== tabToShow && setTabToShow(tabNumber);

  const showModal = (row) => {
    if (row.studentReportAdded) {
      studentRecordTableModalDispatch({
        type: studentRecordTableModalReducerActions.OPEN,
        payload: row,
      });
      return reset(parseDbDataToFormInput(row));
    }
    return addStudentRecordModalDispatch({
      type: addStudentRecordReducerActions.OPEN,
      payload: {
        lessonDetail: row.lessonDetail,
        students: [row],
      },
    });
  };

  const hideModal = () => {
    setTabToShow(initialTab);
    studentRecordTableModalDispatch({
      type: studentRecordTableModalReducerActions.HIDE,
    });
  };

  const [editable, setEditable] = useState(false);

  const saveStudentRecordEdits = () => {
    const data = getValues();
    apiService.lessonRecord.studentRecord
      .update(
        studentRecordTableModalState.studentRecord.id,
        data.studentReport
          ? {
              studentReport: data.studentReport,
              lessonFiles: [
                ...(data.inClassPhotos || []),
                ...(data.classworkPhotos || []),
              ],
            }
          : undefined,
      )
      .then((data2) => {
        createNotification({
          type: "success",
          message: "Succesfully approve lesson report",
        });
        studentRecordTableModalDispatch({
          type: studentRecordTableModalReducerActions.OPEN,
          payload: data2,
        });
        reset(parseDbDataToFormInput(data2));
        setEditable(false);
        setRefetchCheck((check) => !check);
      });
  };

  useEffect(() => {
    setEditable(false);
  }, [studentRecordTableModalState.show]);

  const resetFields = () => reset(studentRecordTableModalState.studentRecord);

  return (
    <div className="content">
      <form
        onSubmit={handleSubmit(saveStudentRecordEdits)}
        id="saveStudentRecordEdits"
      />
      <UploadStudentRecordModal
        studentRecord={uploadStudentRecordModalState.studentRecord}
        show={uploadStudentRecordModalState.show}
        refetch={() => setRefetchCheck((check) => !check)}
        hideModal={() =>
          uploadStudentRecordModalDispatch({
            type: uploadStudentRecordModalActions.HIDE,
          })
        }
      />
      <CustomModal
        show={studentRecordTableModalState.show}
        hideModal={hideModal}
        buttons={
          editable ? (
            <>
              <Button
                type="submit"
                form="saveStudentRecordEdits"
                color="success"
                disabled={
                  studentRecordTableModalState.studentRecord
                    .studentReportApproved
                }
              >
                Save
              </Button>
              <Button
                type="button"
                onClick={() => {
                  resetFields();
                  setEditable(false);
                }}
                color="dark"
                disabled={
                  studentRecordTableModalState.studentRecord
                    .studentReportApproved
                }
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              type="button"
              color="warning"
              disabled={
                studentRecordTableModalState.studentRecord.studentReportApproved
              }
              onClick={(e) => {
                e.preventDefault();
                setEditable(true);
              }}
            >
              {studentRecordTableModalState.studentRecord
                .studentReportApproved && "Cannot"}{" "}
              Edit
            </Button>
          )
        }
        triggerAlertWhenClose={isDirty}
        triggerAlertMessageWhenClose="Confirm close? Changes will be discarded"
      >
        <Nav pills className="nav-pills-primary mt-2">
          {tabs.map((tab) => (
            <NavItem key={tab.index} className="mb-2">
              <NavLink
                className={`rounded px-3 py-1
                                    ${tabToShow === tab.index ? "active" : ""}
                                `}
                onClick={switchTab(tab.index)}
                style={{ cursor: "pointer" }}
              >
                <small>{tab.name}</small>
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={tabToShow} className="mt-4">
          {tabs.map(({ Component }, index) => (
            <Component
              key={index}
              studentRecord={studentRecord}
              type="self"
              {...formMethods}
              editable={editable}
              setEditable={setEditable}
              resetFields={resetFields}
              tabToShow={tabToShow}
              canEdit={false}
            />
          ))}
        </TabContent>
      </CustomModal>
      <AddStudentReportPage
        open={addStudentRecordModalState.show}
        hide={() =>
          addStudentRecordModalDispatch({
            type: addStudentRecordReducerActions.HIDE,
          })
        }
        lessonDetail={addStudentRecordModalState.lessonDetail}
        submitCallback={() => setRefetchCheck((check) => !check)}
      />
      <FilterableTable
        query={(filters) => () =>
          apiService.lessonRecord.studentRecord.self(
            undefined,
            {
              past_records: true,
            },
            filters,
          )
        }
        action={(row) =>
          studentLessonRecordTableActions({
            showModal,
            showUploadModal: (row) =>
              uploadStudentRecordModalDispatch({
                type: uploadStudentRecordModalActions.OPEN,
                payload: row,
              }),
            canAdd: true,
          })
        }
        title="My Lesson Records"
        actionPrepend
        queryKey={["studentRecords", "self"]}
        columns={myLessonRecordsTableColumn(
          studentLessonRecordTableActions({
            showModal,
            showUploadModal: (row) =>
              uploadStudentRecordModalDispatch({
                type: uploadStudentRecordModalActions.OPEN,
                payload: row,
              }),
            canAdd: true,
          }),
        )}
        refetchCheck={refetchCheck}
        filters={[
          {
            filterName: "date_from",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date From",
          },
          {
            filterName: "date_to",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date To",
          },
          { filterName: "centre", operator: "contains", placeholder: "Centre" },
          {
            filterName: "teacher_name",
            operator: "contains",
            placeholder: "Teacher Name",
          },
          {
            filterName: "student_name",
            operator: "contains",
            placeholder: "Student Name",
          },
          {
            filterName: "course_name",
            operator: "contains",
            placeholder: "Subject",
          },
          {
            filterName: "lesson_name",
            operator: "contains",
            placeholder: "Lesson Topic",
          },
          {
            filterName: "is_classwork_added",
            operator: "contains",
            label: "Classwork?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "is_in_class_image_added",
            operator: "contains",
            label: "In-Class Image?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "student_report_approved",
            operator: "contains",
            label: "Approved",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "student_report_added",
            operator: "contains",
            label: "Report",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "attendance_recorded",
            operator: "contains",
            label: "Attendance",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        ]}
      />
    </div>
  );
};

export default MyLessonRecords;
